import { Link } from 'react-router-dom';
import { useConsumerAuth } from '../context/ConsumerAuthContext.jsx';

const steps = [
  {
    title: 'Check your packaging',
    description:
      'Answer a few quick questions to see if your empties meet Pact’s clean-and-empty guidelines.',
  },
  {
    title: 'Choose how to return',
    description:
      'Find a drop-off bin near you or request a prepaid mail-back label—whatever works best.',
  },
  {
    title: 'Track your impact',
    description:
      'We email updates every step of the way so you always know where your empties end up.',
  },
];

function Home() {
  const { user } = useConsumerAuth();

  return (
    <div className="space-y-16">
      <section className="relative overflow-hidden rounded-[48px] border border-base-200 bg-gradient-to-br from-primary/10 via-base-100 to-base-100 p-10 shadow-xl sm:p-12">
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 hidden w-1/3 translate-x-1/4 rounded-full bg-primary/20 blur-3xl lg:block"
        />
        <div className="relative grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-7">
            <span className="inline-flex items-center rounded-full bg-primary/20 px-4 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
              Circular beauty with Pact
            </span>
            <div className="space-y-4">
              <h1 className="text-4xl font-semibold text-primary sm:text-5xl">
                {user ? `Welcome back, ${user.name.split(' ')[0]}!` : 'Give your beauty empties a second life.'}
              </h1>
              <p className="max-w-xl text-base text-[#455855] sm:text-lg">
                Pact makes it simple to recycle the beauty packaging that usually gets landfilled. Verify what you have,
                choose how to return it, and keep tabs on your impact—all in one place.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/verify" className="btn btn-primary btn-lg rounded-full shadow-md shadow-primary/20">
                Start verification
              </Link>
              {user ? (
                <Link
                  to="/my-impact"
                  className="btn btn-ghost rounded-full border border-primary/30 px-6 text-primary hover:bg-primary/10"
                >
                  View my impact
                </Link>
              ) : (
                <Link
                  to="/signup"
                  className="btn btn-ghost rounded-full border border-primary/30 px-6 text-primary hover:bg-primary/10"
                >
                  Create an account
                </Link>
              )}
            </div>
            <dl className="grid gap-4 text-sm text-[#536360] sm:grid-cols-2">
              <div className="space-y-1">
                <dt className="font-semibold text-primary">1k+ drop-off partners</dt>
                <dd>Beauty retailers across North America host Pact bins for hard-to-recycle empties.</dd>
              </div>
              <div className="space-y-1">
                <dt className="font-semibold text-primary">Mail-back when you need it</dt>
                <dd>Request a prepaid USPS label and recycle right from home if a bin isn&apos;t nearby.</dd>
              </div>
            </dl>
          </div>
          <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-base-100 p-8 shadow-lg">
            <div aria-hidden className="absolute -top-12 right-[-40px] h-40 w-40 rounded-full bg-primary/10 blur-2xl" />
            <div className="relative space-y-5">
              <h2 className="text-xl font-semibold text-primary">How Pact works</h2>
              <ul className="space-y-3 text-sm text-[#495a57]">
                <li className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    1
                  </span>
                  Confirm your packaging is clean, dry, and Pact-ready.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    2
                  </span>
                  Drop it at a Pact bin—or request a mail-back label that meets you at home.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    3
                  </span>
                  Track updates every step of the way once your empties are processed.
                </li>
              </ul>
              <Link to="/learn" className="btn btn-outline rounded-full">
                See Pact guidelines
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <article className="rounded-3xl border border-base-200 bg-base-100 p-8 shadow-md">
          <h2 className="text-3xl font-semibold text-primary">What you&apos;ll need</h2>
          <ul className="mt-6 space-y-4 text-sm text-[#475654]">
            <li className="flex gap-4">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-sm font-semibold text-primary">
                1
              </span>
              Packaging details: brand + product name help us match your item quickly.
            </li>
            <li className="flex gap-4">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-sm font-semibold text-primary">
                2
              </span>
              Your ZIP code so we can surface drop-off options near you.
            </li>
            <li className="flex gap-4">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-sm font-semibold text-primary">
                3
              </span>
              A clean-and-empty confirmation—Pact only accepts rinsed, dry packaging.
            </li>
          </ul>
        </article>
        <article className="rounded-3xl border border-primary/20 bg-primary/10 p-8 shadow-md">
          <h2 className="text-3xl font-semibold text-primary">How it works</h2>
          <ol className="mt-6 space-y-4 text-sm text-[#435351]">
            {steps.map((step, index) => (
              <li key={step.title} className="flex gap-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-content shadow-lg shadow-primary/30">
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium text-primary">{step.title}</p>
                  <p>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </article>
      </section>

      <section className="rounded-3xl border border-base-200 bg-base-100 p-8 shadow-md">
        <h2 className="text-3xl font-semibold text-primary">Already requested a mail-back label?</h2>
        <p className="mt-3 text-sm text-[#475654]">
          Enter the email you used and track your package status once it&apos;s on the way. To keep everything synced,
          reuse the same verification session before generating additional labels.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link to="/mail-back" className="btn btn-primary rounded-full">
            View mail-back status
          </Link>
          <Link to="/verify" className="link link-primary text-sm">
            Return to verification
          </Link>
        </div>
      </section>

      <section className="rounded-3xl border border-dashed border-primary/40 bg-primary/10 p-8 shadow-sm">
        <h2 className="text-3xl font-semibold text-primary">New to Pact?</h2>
        <p className="mt-3 text-sm text-[#435351]">
          Learn how the Pact program works, what packaging we accept, and how your empties turn into better recycling
          outcomes.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link to="/learn" className="btn btn-outline rounded-full">
            Explore Pact education
          </Link>
          <a
            href="https://www.pactcollective.org/program"
            target="_blank"
            rel="noreferrer"
            className="link link-primary text-sm"
          >
            Visit pactcollective.org
          </a>
        </div>
      </section>

      <section className="rounded-[32px] border border-primary/20 bg-primary/10 p-8 shadow-md">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <h2 className="text-3xl font-semibold text-primary">Know a Pact-ready item already?</h2>
            <p className="text-sm text-[#3d4d4a]">
              Save items you know fit our guidelines and move straight to bin finding or mail-back next time—your
              verification list stays at your fingertips.
            </p>
            <Link to="/verify" className="btn btn-primary rounded-full">
              Go to verification
            </Link>
          </div>
          <div className="space-y-4 text-sm text-[#2f3b3a]">
            <figure className="rounded-2xl border border-primary/20 bg-base-100/80 p-5 shadow-sm">
              “We already trust Pact to take our packaging. Knowing what’s accepted makes it so easy to keep doing it.”
            </figure>
            <figure className="rounded-2xl border border-primary/20 bg-base-100/80 p-5 shadow-sm">
              “Mail-back labels mean I can recycle from home when I don’t have time to visit a drop-off bin.”
            </figure>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
